import { z } from 'zod'

// Common field schemas
const titleSchema = z.string()
  .min(3, 'Title must be at least 3 characters')
  .max(100, 'Title must be less than 100 characters')

const descriptionSchema = z.string()
  .max(5000, 'Description must be less than 5000 characters')

// A group of checkboxes registered under one name in react-hook-form yields an
// array of the checked values — EXCEPT when none are checked, where RHF passes
// `false` (a boolean), not `[]`. That happens on intangible fundraisers
// (sponsorship / raffle / donation) where the seller ticks no shipping method,
// and made submission fail at step 4 with "expected array, received boolean"
// (bug icODwKMet). Coerce a non-array back to an empty array.
const checkboxStringArray = z.preprocess(
  (v) => (Array.isArray(v) ? v : v == null || v === false ? [] : v),
  z.array(z.string()).nullish(),
)

// Base listing schema
export const baseListingSchema = z.object({
  title: titleSchema,
  description: descriptionSchema,
  price: z.preprocess(
    (val) => (val === '' || val === null || val === undefined || Number.isNaN(val) ? undefined : Number(val)),
    z.number({ required_error: 'Price is required', invalid_type_error: 'Please enter a valid price' })
      .min(0, 'Price must be positive')
      .max(100000000, 'Price exceeds maximum')
      .optional(),
  ),
  category: z.string().min(1, 'Please select a category'),
  listingType: z.enum(['general', 'vehicle', 'fundraiser'] as const),
  saleType: z.enum(['buynow', 'auction', 'product'] as const).optional(),
  condition: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['new', 'excellent', 'good', 'fair', 'used'] as const).optional()),
  location: z.string().min(1, 'Please select a region'),
  suburb: z.string().optional(),
  // Auction form fields (only used when saleType === 'auction')
  auctionDuration: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(1).max(30).optional()),
  reservePrice: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).optional()),
  buyNowPrice: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).optional()),
  // Product form fields (only used when saleType === 'product')
  stock: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).max(1000000).optional()),
  sku: z.string().optional(),
  // Buyer-flow toggles (independent of saleType)
  enableOffers: z.boolean().optional(),
  // Delivery methods ticked by the seller (Pickup / Courier / Post). MUST be in
  // the schema — otherwise zod strips it on parse, the save code reads an empty
  // array, and the per-method shipping costs get nulled out (bug d30TjrX2).
  deliveryOptions: checkboxStringArray,
  // Shipping costs (set when Courier/Post delivery methods are ticked)
  shippingCostCourier: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0, 'Shipping cost cannot be negative').max(1000, 'Shipping cost exceeds maximum').optional()),
  shippingCostPost: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0, 'Shipping cost cannot be negative').max(1000, 'Shipping cost exceeds maximum').optional()),
})

/**
 * Conditional-required rule for per-method shipping costs: when the seller ticks
 * Courier or Post, they must enter that method's cost (the UI marks it required
 * with a *, but the field itself is optional so blank-but-ticked would otherwise
 * publish and then break courier/post checkout). Apply via `withShippingCosts()`.
 */
function refineShippingCosts(
  data: { deliveryOptions?: unknown; shippingCostCourier?: number | null; shippingCostPost?: number | null },
  ctx: z.RefinementCtx,
): void {
  const opts = Array.isArray(data.deliveryOptions) ? (data.deliveryOptions as string[]) : []
  if (opts.includes('Courier') && data.shippingCostCourier == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['shippingCostCourier'], message: 'Enter a courier cost, or untick Courier' })
  }
  if (opts.includes('Post') && data.shippingCostPost == null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['shippingCostPost'], message: 'Enter an NZ Post cost, or untick Post' })
  }
}

/** Wrap a listing schema with the conditional-required shipping-cost rule. */
export function withShippingCosts<T extends z.ZodTypeAny>(schema: T) {
  return schema.superRefine((data, ctx) => refineShippingCosts(data as never, ctx))
}

// Vehicle-specific schema
export const vehicleSchema = baseListingSchema.extend({
  listingType: z.literal('vehicle'),
  make: z.string().min(1, 'Make is required'),
  model: z.string().min(1, 'Model is required'),
  year: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number({ required_error: 'Year is required' }).min(1900, 'Year must be 1900 or later').max(new Date().getFullYear() + 1, 'Year cannot be in the future')),
  odometer: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number({ required_error: 'Odometer is required' }).min(0, 'Odometer must be positive')),
  colour: z.string().min(1, 'Colour is required'),
  bodyType: z.string().optional(),
  doors: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(1).max(10).optional()),
  engineCC: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).optional()),
  fuelType: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['Petrol', 'Diesel', 'Electric', 'Hybrid', 'LPG'] as const).optional()),
  transmission: z.preprocess((v) => (v === '' ? undefined : v), z.enum(['Automatic', 'Manual'] as const).optional()),
  previousOwners: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(0).max(99).optional()),
  wofExpiry: z.string().optional(),
  regoExpiry: z.string().optional(),
  // 2026-05-25: new fields ported from legacy iSQROLL automotive form.
  modelDetail: z.string().optional(),
  importHistory: z.preprocess((v) => (v === '' ? undefined : v), z.string().optional()),
  seats: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(1).max(50).optional()),
  cylinders: z.preprocess((v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)), z.number().min(1).max(16).optional()),
  plateOrVin: z.string().optional(),
  reportedStolen: z.preprocess((v) => v === '' ? undefined : v === 'true' ? true : v === 'false' ? false : v, z.boolean().optional()),
  importedDamaged: z.preprocess((v) => v === '' ? undefined : v === 'true' ? true : v === 'false' ? false : v, z.boolean().optional()),
  onRoadCostsIncluded: z.preprocess((v) => v === '' ? undefined : v === 'true' ? true : v === 'false' ? false : v, z.boolean().optional()),
})

// Auction-specific fields
export const auctionSchema = z.object({
  saleType: z.literal('auction'),
  startingPrice: z.number().min(0, 'Starting price must be positive'),
  reservePrice: z.number().min(0).optional(),
  duration: z.number().min(1).max(30, 'Duration must be between 1-30 days'),
})

// General listing schema
export const generalListingSchema = baseListingSchema.extend({
  listingType: z.literal('general'),
})

// Fundraiser product schema
export const fundraiserProductSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Product name is required'),
  price: z.number().min(0, 'Price must be positive'),
  description: z.string().optional(),
  image: z.string().optional(),
  stock: z.number().min(0).optional(),
})

// Raffle prize schema
export const rafflePrizeSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Prize name is required'),
  description: z.string().optional(),
  value: z.number().min(0).optional(),
  image: z.string().optional(),
})

// Sponsorship tier schema
export const sponsorshipTierSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'Tier name is required'),
  amount: z.number().min(0, 'Amount must be positive'),
  description: z.string().min(1, 'Description is required'),
  benefits: z.array(z.string()).default([]),
  limit: z.number().min(0).optional(),
})

// Number fields registered via `valueAsNumber: true` pass `null` for empty
// inputs — wrap with preprocess so Zod treats empty/null/NaN as "not provided".
const optionalNumber = (min = 0) =>
  z.preprocess(
    (v) => (v === '' || v === null || v === undefined || Number.isNaN(v) ? undefined : Number(v)),
    z.number().min(min).optional(),
  )

// Fundraiser-specific schema
export const fundraiserSchema = baseListingSchema.extend({
  listingType: z.literal('fundraiser'),
  fundraiserType: z.enum(['auction', 'products', 'raffle', 'sponsorship'] as const),
  organizationName: z.string().min(1, 'Organization name is required'),
  closingDate: z.string().optional(),
  targetAmount: optionalNumber(0),
  // Auction-specific
  bidIncrement: optionalNumber(0),
  deliveryOptions: checkboxStringArray,
  // Products
  products: z.array(fundraiserProductSchema).nullish(),
  // Raffle
  ticketPrice: optionalNumber(0),
  totalTickets: optionalNumber(1),
  maxTicketsPerPerson: optionalNumber(1),
  prizes: z.array(rafflePrizeSchema).nullish(),
  termsAndConditions: z.string().max(5000).nullish(),
  // Sponsorship
  tiers: z.array(sponsorshipTierSchema).nullish(),
  sponsorRecognition: z.string().max(2000).nullish(),
}).superRefine((data, ctx) => {
  // Sponsorship fundraisers must have at least one tier so buyers have something
  // to actually sponsor. Without tiers the listing page would render no
  // actionable UI for backers.
  if (data.fundraiserType === 'sponsorship' && (!data.tiers || data.tiers.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tiers'],
      message: 'Add at least one sponsorship tier so backers know how to support you',
    })
  }
  // Products fundraisers need at least one product
  if (data.fundraiserType === 'products' && (!data.products || data.products.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['products'],
      message: 'Add at least one product for backers to buy',
    })
  }
})

// Store slug schema (URL-safe identifier for /store/slug routes)
export const storeSlugSchema = z.string()
  .min(3, 'Store URL must be at least 3 characters')
  .max(40, 'Store URL must be less than 40 characters')
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Only lowercase letters, numbers, and hyphens (no leading/trailing hyphens)')
  .refine(s => !s.includes('--'), 'No consecutive hyphens allowed')

// Profile schema
export const profileSchema = z.object({
  displayName: z.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters'),
  bio: z.string().max(500, 'Bio must be less than 500 characters').optional(),
  location: z.string().optional(),
  phone: z.string()
    .regex(/^(\+64|0)?[2-9]\d{7,9}$/, 'Please enter a valid NZ phone number')
    .optional()
    .or(z.literal('')),
  showEmail: z.boolean().optional(),
  showPhone: z.boolean().optional(),
  showEmailOnListings: z.boolean().optional(),
  enableChat: z.boolean().optional(),
  enableEnquiryForm: z.boolean().optional(),
  storeSlug: storeSlugSchema.optional().or(z.literal('')),
})

// Strong password schema (for signup and password change)
export const strongPasswordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Must contain at least one number')

// Login schema
export const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

// Signup schema
export const signupSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: strongPasswordSchema,
  confirmPassword: z.string(),
  displayName: z.string().min(2, 'Name must be at least 2 characters'),
  acceptTerms: z.boolean().refine(val => val === true, 'You must accept the terms'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

// Change password schema
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: strongPasswordSchema,
  confirmNewPassword: z.string(),
}).refine(data => data.newPassword === data.confirmNewPassword, {
  message: 'Passwords do not match',
  path: ['confirmNewPassword'],
})

// Bug report schema.
// The three guided text fields (stepsTaken / expectedResult / actualResult) are
// relabelled per category in the form. stepsTaken is the always-required primary
// field; actualResult ("what actually happened") only makes sense for a bug, so
// it's required for category 'bug' and optional for feature / feedback / other.
export const bugReportSchema = z
  .object({
    title: z.string().min(5, 'Title must be at least 5 characters').max(200, 'Title must be less than 200 characters'),
    category: z.enum(['bug', 'feature', 'feedback', 'other'] as const),
    priority: z.enum(['low', 'medium', 'high', 'critical'] as const),
    stepsTaken: z.string().min(10, 'Please add a bit more detail (at least 10 characters)').max(5000),
    expectedResult: z.string().max(5000).optional(),
    actualResult: z.string().max(5000).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.category === 'bug' && (data.actualResult ?? '').trim().length < 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['actualResult'],
        message: 'Please describe what actually happened (at least 10 characters)',
      })
    }
  })

// Offer schema
export const offerSchema = z.object({
  amount: z
    .number({ invalid_type_error: 'Please enter an offer amount', required_error: 'Please enter an offer amount' })
    .min(1, 'Offer must be at least $1'),
  message: z.string().max(500, 'Message must be less than 500 characters').optional(),
})

// Review schema
export const reviewSchema = z.object({
  rating: z.number().min(1, 'Please select a rating').max(5),
  comment: z.string().min(10, 'Review must be at least 10 characters').max(2000),
})

// Type exports
export type BaseListingFormData = z.infer<typeof baseListingSchema>
export type VehicleFormData = z.infer<typeof vehicleSchema>
export type GeneralFormData = z.infer<typeof generalListingSchema>
export type FundraiserFormData = z.infer<typeof fundraiserSchema>
export type ProfileFormData = z.infer<typeof profileSchema>
export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>
